import * as React from 'react'
import {
  createSelector as reselectCreateSelector,
  Selector,
  SelectorResultArray,
} from 'reselect'

import type { GridReactTableInstance } from '../models/gridReactTable'
import type { DataGridProcessedProps } from '../models/props/DataGridProps'

type CacheKey = number | string

interface CacheContainer {
  cache: Record<CacheKey, Map<any[], any>> | null
}

export interface OutputSelector<State, Result> {
  (apiRef: React.MutableRefObject<{ state: State; instanceId: number }>): Result
  // TODO v6: make instanceId require
  (state: State, instanceId?: number): Result
  acceptsApiRef: boolean
}

export interface OutputSelectorAdvanced<State, TableInstance, Props, Result> {
  (apiRef: React.MutableRefObject<{ state: State; instanceId: number }>): Result
  // TODO v6: make instanceId require
  (
    state: State,
    tableInstance: TableInstance,
    Props: Props,
    instanceId?: number
  ): Result
  acceptsApiRef: boolean
}

type StateFromSelector<T> = T extends (first: infer F, ...args: any[]) => any
  ? F extends { state: infer F2 }
    ? F2
    : F
  : never

type StateFromSelectorList<Selectors extends readonly any[]> =
  Selectors extends [f: infer F, ...rest: infer R]
    ? StateFromSelector<F> extends StateFromSelectorList<R>
      ? StateFromSelector<F>
      : StateFromSelectorList<R>
    : {}

type SelectorArgs<Selectors extends ReadonlyArray<Selector<any>>, Result> =
  // Input selectors as a separate array
  | [
      selectors: [...Selectors],
      combiner: (...args: SelectorResultArray<Selectors>) => Result,
    ]
  // Input selectors as separate inline arguments
  | [...Selectors, (...args: SelectorResultArray<Selectors>) => Result]

type CreateSelectorFunction = <
  Selectors extends ReadonlyArray<Selector<any>>,
  Result,
>(
  ...items: SelectorArgs<Selectors, Result>
) => OutputSelector<StateFromSelectorList<Selectors>, Result>

type CreateSelectorFunctionAdvance = <
  Selectors extends ReadonlyArray<Selector<any>>,
  Result,
>(
  extractTableInstance: (inst: GridReactTableInstance) => any,
  extractGridProps: (props: DataGridProcessedProps) => any,
  ...items: SelectorArgs<Selectors, Result>
) => OutputSelector<StateFromSelectorList<Selectors>, Result>

const cacheContainer: CacheContainer = { cache: null }

export const createSelector: CreateSelectorFunction = (...args: any) => {
  if (cacheContainer.cache === null) {
    cacheContainer.cache = {}
  }

  const selector = (...selectorArgs: any[]) => {
    const [stateOrApiRef, instanceId] = selectorArgs
    const isApiRef = !!stateOrApiRef.current
    const cacheKey = isApiRef
      ? stateOrApiRef.current.instanceId
      : instanceId ?? 'default'
    const state = isApiRef ? stateOrApiRef.current.state : stateOrApiRef

    if (cacheContainer.cache === null) {
      cacheContainer.cache = {}
    }

    const { cache } = cacheContainer

    if (cache[cacheKey] && cache?.[cacheKey]?.get(args)) {
      // We pass the cache key because the called selector might have as
      // dependency another selector created with this `createSelector`.
      return cache?.[cacheKey]?.get(args)(state, cacheKey)
    }

    const newSelector = reselectCreateSelector(...args)

    if (!cache[cacheKey]) {
      cache[cacheKey] = new Map()
    }
    cache?.[cacheKey]?.set(args, newSelector)

    return newSelector(state, cacheKey)
  }

  // We use this property to detect if the selector was created with createSelector
  // or it's only a simple function the receives the state and returns part of it.
  selector.acceptsApiRef = true

  return selector
}

export const createSelectorAdvanced: CreateSelectorFunctionAdvance = (
  extractTableInstance = () => null,
  extractGridProps = () => null,
  ...args: any
) => {
  if (cacheContainer.cache === null) {
    cacheContainer.cache = {}
  }

  const selector = (...selectorArgs: any[]) => {
    const [stateOrApiRef, tableInstance, props, instanceId] = selectorArgs
    const isApiRef = !!stateOrApiRef.current
    const cacheKey = isApiRef
      ? stateOrApiRef.current.instanceId
      : instanceId ?? 'default'
    const state = isApiRef ? stateOrApiRef.current.state : stateOrApiRef

    const tableInstanceValues =
      typeof extractTableInstance === 'function'
        ? extractTableInstance(tableInstance)
        : null
    const gridPropsValues =
      typeof extractGridProps === 'function' ? extractGridProps(props) : null

    if (cacheContainer.cache === null) {
      cacheContainer.cache = {}
    }

    const { cache } = cacheContainer

    if (cache[cacheKey] && cache?.[cacheKey]?.get(args)) {
      // We pass the cache key because the called selector might have as
      // dependency another selector created with this `createSelector`.
      return cache?.[cacheKey]?.get(args)(
        state,
        tableInstanceValues,
        gridPropsValues,
        cacheKey
      )
    }

    const newSelector = reselectCreateSelector(...args)

    if (!cache[cacheKey]) {
      cache[cacheKey] = new Map()
    }
    cache?.[cacheKey]?.set(args, newSelector)

    return newSelector(state, tableInstanceValues, gridPropsValues, cacheKey)
  }

  // We use this property to detect if the selector was created with createSelector
  // or it's only a simple function the receives the state and returns part of it.
  selector.acceptsApiRef = true

  return selector
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const unstable_resetCreateSelectorCache = (cacheKey?: CacheKey) => {
  if (typeof cacheKey !== 'undefined') {
    if (cacheContainer.cache && cacheContainer.cache[cacheKey]) {
      delete cacheContainer.cache[cacheKey]
    }
  } else {
    cacheContainer.cache = null
  }
}
